import * as React from "react";
import * as toastr from "toastr";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { FormInput } from "source/app/presentation/Shared/Form/Input/FormInput";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import RenderSwal, {
    UnmountAllSwals,
} from "source/app/services/Shared/RenderSwal/RenderSwal";
import { SwalButtons } from "source/app/services/Shared/RenderSwal/SwalBody/SwalButtons";

export function ChangePassword() {
    return (
        <IconWithText
            iconSuffix={EGlyphicon.lock}
            whenClicked={ShowChangePassword}
            displayAsBlock={false}
            tooltip="Change password"
        />
    );
}

export function ShowChangePassword() {
    RenderSwal(
        {
            height: "250px",
            maxWidth: "360px",
        },
        <ChangePasswordForm />
    );
}

function ChangePasswordForm() {
    const [currentPassword, setCurrentPassword] = React.useState("");
    const [newPassword, setNewPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [isSaving, setIsSaving] = React.useState(false);

    const canSave =
        currentPassword !== "" &&
        newPassword !== "" &&
        confirmPassword !== "" &&
        !isSaving;

    const save = async () => {
        const validationError = GetPasswordChangeValidationError({
            currentPassword,
            newPassword,
            confirmPassword,
        });

        if (validationError) {
            toastr.error(validationError, "Password change");
            return;
        }

        if (isSaving) {
            return;
        }

        try {
            setIsSaving(true);
            await ClientDataProvider.ChangePassword(
                currentPassword,
                newPassword
            );
            toastr.success("Password changed");
            UnmountAllSwals();
        } catch (error) {
            toastr.error(
                GetPasswordChangeErrorMessage(error),
                "Password change failed"
            );
            setIsSaving(false);
        }
    };

    return (
        <div className={formClass}>
            <div className={titleClass}>Change password</div>
            <FormInput
                id="changePassword_currentPassword"
                type="password"
                placeholder="current password"
                autocomplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                onEnter={save}
                displayBlock={true}
                showEnterHint={false}
            />
            <FormInput
                id="changePassword_newPassword"
                type="password"
                placeholder="new password"
                autocomplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                onEnter={save}
                displayBlock={true}
                showEnterHint={false}
            />
            <FormInput
                id="changePassword_confirmPassword"
                type="password"
                placeholder="confirm new password"
                autocomplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onEnter={save}
                displayBlock={true}
                showEnterHint={false}
            />
            <div className={saveButtonContainerClass}>
                <SwalButtons.Save
                    onClick={save}
                    disabled={!canSave}
                    label={isSaving ? "Saving" : "Save"}
                />
                <SwalButtons.Cancel Cancel={UnmountAllSwals} />
            </div>
        </div>
    );
}

function GetPasswordChangeValidationError({
    currentPassword,
    newPassword,
    confirmPassword,
}: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}) {
    if (!currentPassword || !newPassword || !confirmPassword) {
        return "Please complete every password field";
    }

    if (newPassword !== confirmPassword) {
        return "New password confirmation does not match";
    }

    if (currentPassword === newPassword) {
        return "New password must be different";
    }

    return "";
}

function GetPasswordChangeErrorMessage(error: unknown) {
    const fallback = "Unable to change password";

    if (!(error instanceof Error)) {
        return fallback;
    }

    const responseBody = error.message.split(" - ").pop();

    if (!responseBody) {
        return error.message || fallback;
    }

    try {
        const parsed = JSON.parse(responseBody);
        return parsed.error || parsed.message || error.message;
    } catch {
        return error.message || fallback;
    }
}

const formClass = StyleClass("changePasswordForm", {
    color: "white",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "8px 8px 0",
});

const titleClass = StyleClass("changePasswordTitle", {
    fontSize: "16px",
    fontWeight: "bold",
    textAlign: "center",
});

const saveButtonContainerClass = StyleClass("changePasswordSaveButton", {
    display: "flex",
    justifyContent: "center",
    opacity: 1,
});
